import { Check, Lock } from 'lucide-react';

import { cn } from '@sandtech-hub-monorepo/ui-kit';

import { Module, sizeLabel } from '../../data/programs';
import { AccessChip } from './AccessChip';

interface Props {
  index: number;
  module: Module;
  /** Whether paid modules are currently openable (a covering subscription). */
  paidUnlocked?: boolean;
}

/**
 * One module of a course: the leaf of the informative tree.
 *
 * A module is Savanna's block, and it does not open. Its activities sit one
 * level below in Savanna but go unrendered here - they are read to count and
 * to place the module on the free/partially-free/paid scale, which is what
 * someone weighing the program up needs from this list. Listing their names
 * would add length without adding that.
 *
 * The badge is the point of the row. A module mixes free and paywalled
 * activities, so `Partially Free` says something definite about this module
 * rather than summarising a level below it.
 */
export const ModuleRow = ({ index, module, paidUnlocked = false }: Props) => {
  // Partially free is still a module you can start: part of it is open, and
  // the subscription is what finishes it. Only a fully paid module is shut.
  const accessible = module.access !== 'paid' || paidUnlocked;
  const size = sizeLabel(module);

  return (
    <div className="flex items-center gap-3 py-2.5 text-sm">
      <span className="w-5 shrink-0 text-muted-foreground">{index}.</span>
      <span
        className={cn(
          'min-w-0 flex-1 truncate',
          accessible ? 'text-foreground' : 'text-muted-foreground'
        )}
      >
        {module.title}
      </span>

      {/*
        Only ever true for a flow Savanna resolved for a learner. It is
        undefined on the catalogue read rather than false, so an unenrolled
        visitor sees no completion state at all instead of every module
        marked unfinished.
      */}
      {module.completed && (
        <Check
          className="h-4 w-4 shrink-0 text-primary"
          aria-label="Completed"
        />
      )}
      {size && <span className="shrink-0 text-muted-foreground">{size}</span>}
      {!accessible && (
        <Lock
          className="h-4 w-4 shrink-0 text-saffron-active"
          aria-label="Needs a subscription"
        />
      )}
      {!paidUnlocked && <AccessChip access={module.access} />}
    </div>
  );
};
